import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Spinner,
  IconButton,
  Badge,
  Alert,
  AlertIcon,
  SimpleGrid,
  Flex,
  Stack,
  Text,
  useDisclosure,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tooltip,
  useColorModeValue
} from '@chakra-ui/react';
import {
  FaChevronDown,
  FaEye,
  FaTrash,
  FaSearch,
  FaTimes,
  FaMapMarkerAlt
} from 'react-icons/fa';
import AdminService from '../../services/AdminService';

const SearchHistory = ({ userId }) => {
  const [searchHistory, setSearchHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [filters, setFilters] = useState({
    user_id: '',
    search_type: ''
  });
  const { isOpen: isDetailsModalOpen, onOpen: onDetailsModalOpen, onClose: onDetailsModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();
  const bgColor = useColorModeValue('white', 'gray.700');

  // Set user_id filter when userId prop changes
  useEffect(() => {
    if (userId) {
      setFilters(prev => ({
        ...prev,
        user_id: userId
      }));
    }
  }, [userId]);

  // Fetch search history on component mount and when page, rowsPerPage, or filters change
  useEffect(() => {
    const fetchSearchHistory = async () => {
      setLoading(true);
      try {
        // Filter out empty filter values
        const activeFilters = Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '')
        );
        
        const response = await AdminService.getSearchHistory(page + 1, rowsPerPage, activeFilters);
        setSearchHistory(response.history);
        setTotalItems(response.total);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching search history:', err);
        setError('Failed to load search history. Please try again later.');
        setLoading(false);
      }
    };

    fetchSearchHistory();
  }, [page, rowsPerPage, filters]);

  // Handle page change
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(parseInt(value, 10));
    setPage(0);
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
    setPage(0);
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      user_id: '',
      search_type: ''
    });
    setPage(0);
  };

  // View details of a search history item
  const handleViewDetails = async (id) => {
    try {
      setLoading(true);
      const item = await AdminService.getSearchHistoryItem(id);
      setSelectedItem(item);
      onDetailsModalOpen();
      setLoading(false);
    } catch (err) {
      console.error('Error fetching search history details:', err);
      setError('Failed to load search details. Please try again later.');
      setLoading(false);
    }
  };

  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (item) => {
    setSelectedItem(item);
    onDeleteModalOpen();
  };

  // Delete search history item
  const handleDeleteItem = async () => {
    try {
      await AdminService.deleteSearchHistoryItem(selectedItem.id);
      
      // Update the local state
      setSearchHistory(searchHistory.filter(item => item.id !== selectedItem.id));
      setTotalItems(totalItems - 1);
      
      setSuccessMessage('Search history item deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      onDeleteModalClose();
    } catch (err) {
      console.error('Error deleting search history item:', err);
      setError('Failed to delete search history item. Please try again.');
      setTimeout(() => setError(null), 3000);
      onDeleteModalClose();
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Format address for display
  const formatAddress = (item) => {
    const parts = [];
    if (item.flat_number) parts.push(`Flat ${item.flat_number}`);
    if (item.street_number) parts.push(item.street_number);
    if (item.street) parts.push(item.street);
    if (item.postcode) parts.push(item.postcode);
    
    return parts.join(', ') || item.full_address || 'N/A';
  };

  return (
    <Box>
      <Heading as="h2" size="md" mb={4}>
        Search History
      </Heading>
      
      {successMessage && (
        <Alert status="success" mb={4}>
          <AlertIcon />
          {successMessage}
        </Alert>
      )}
      
      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}
      
      {/* Filters */}
      <Box bg={bgColor} p={4} mb={4} borderRadius="md" shadow="md">
        <Heading size="sm" mb={3}>
          Filters
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} alignItems="end">
          <FormControl>
            <FormLabel>User ID</FormLabel>
            <Input
              name="user_id"
              value={filters.user_id}
              onChange={handleFilterChange}
              type="number"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Search Type</FormLabel>
            <Select
              name="search_type"
              value={filters.search_type}
              onChange={handleFilterChange}
            >
              <option value="">All</option>
              <option value="free">Free</option>
              <option value="premium">Premium</option>
              <option value="address">Address</option>
              <option value="postcode">Postcode</option>
            </Select>
          </FormControl>
          <Button
            colorScheme="blue"
            variant="outline"
            onClick={handleResetFilters}
            width="full"
          >
            Reset Filters
          </Button>
        </SimpleGrid>
      </Box>
      
      <Box bg={bgColor} shadow="md" borderRadius="md" overflow="hidden">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>User</Th>
              <Th>Address</Th>
              <Th>Type</Th>
              <Th>Timestamp</Th>
              <Th>Property Type</Th>
              <Th>Growth Rate</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={8} textAlign="center">
                  <Spinner size="md" my={4} />
                </Td>
              </Tr>
            ) : searchHistory.length === 0 ? (
              <Tr>
                <Td colSpan={8} textAlign="center">
                  No search history found
                </Td>
              </Tr>
            ) : (
              searchHistory.map((item) => (
                <Tr key={item.id}>
                  <Td>{item.id}</Td>
                  <Td>
                    <Tooltip label={item.user_email}>
                      <Box>
                        <Text>{item.user_id}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {item.user_email?.split('@')[0]}...
                        </Text>
                      </Box>
                    </Tooltip>
                  </Td>
                  <Td>
                    <Tooltip label={formatAddress(item)}>
                      <Text noOfLines={1}>
                        {formatAddress(item)}
                      </Text>
                    </Tooltip>
                  </Td>
                  <Td>
                    <Badge 
                      colorScheme={item.search_type === 'premium' ? 'purple' : 'gray'}
                      variant="solid"
                    >
                      {item.search_type}
                    </Badge>
                  </Td>
                  <Td>{formatDate(item.search_timestamp)}</Td>
                  <Td>{item.property_type || 'N/A'}</Td>
                  <Td>
                    {item.annual_growth_rate ? `${item.annual_growth_rate}%` : 'N/A'}
                  </Td>
                  <Td>
                    <IconButton
                      size="sm"
                      colorScheme="blue"
                      icon={<FaEye />}
                      onClick={() => handleViewDetails(item.id)}
                      aria-label="View details"
                      mr={2}
                    />
                    <IconButton
                      size="sm"
                      colorScheme="red"
                      icon={<FaTrash />}
                      onClick={() => handleOpenDeleteDialog(item)}
                      aria-label="Delete item"
                    />
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
        
        <Flex justify="space-between" align="center" p={4}>
          <Box>
            <Select
              value={rowsPerPage}
              onChange={(e) => handleChangeRowsPerPage(e.target.value)}
              size="sm"
              width="100px"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </Select>
          </Box>
          
          <Flex>
            <Button
              size="sm"
              onClick={() => handleChangePage(page - 1)}
              isDisabled={page === 0}
              mr={2}
            >
              Previous
            </Button>
            <Text alignSelf="center" mx={2}>
              Page {page + 1} of {Math.ceil(totalItems / rowsPerPage)}
            </Text>
            <Button
              size="sm"
              onClick={() => handleChangePage(page + 1)}
              isDisabled={page >= Math.ceil(totalItems / rowsPerPage) - 1}
              ml={2}
            >
              Next
            </Button>
          </Flex>
        </Flex>
      </Box>
      
      {/* Details Modal */}
      <Modal 
        isOpen={isDetailsModalOpen} 
        onClose={onDetailsModalClose}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Search Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedItem && (
              <Box pt={2}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">User</Text>
                    <Text mb={2}>
                      {selectedItem.user_id} ({selectedItem.user_email})
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Timestamp</Text>
                    <Text mb={2}>
                      {formatDate(selectedItem.search_timestamp)}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Full Address</Text>
                    <Text mb={2}>
                      {selectedItem.full_address}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Search Type</Text>
                    <Text mb={2}>
                      {selectedItem.search_type}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Property Type</Text>
                    <Text mb={2}>
                      {selectedItem.property_type || 'N/A'}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Coordinates</Text>
                    <Text mb={2}>
                      {selectedItem.latitude ? `${selectedItem.latitude}, ${selectedItem.longitude}` : 'N/A'}
                    </Text>
                  </Box>
                </SimpleGrid>
                
                <Heading as="h3" size="sm" mt={6} mb={4}>
                  Property Analysis
                </Heading>
                
                <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Annual Growth Rate</Text>
                    <Text mb={2}>
                      {selectedItem.annual_growth_rate ? `${selectedItem.annual_growth_rate}%` : 'N/A'}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Planning Success Rate</Text>
                    <Text mb={2}>
                      {selectedItem.planning_success_rate ? `${selectedItem.planning_success_rate}%` : 'N/A'}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Crime Ratio</Text>
                    <Text mb={2}>
                      {selectedItem.crime_ratio ? `${selectedItem.crime_ratio}` : 'N/A'}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Community Rating</Text>
                    <Text mb={2}>
                      {selectedItem.community_rating || 'N/A'}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold" fontSize="sm">Monthly Cost</Text>
                    <Text mb={2}>
                      {selectedItem.monthly_cost ? `£${selectedItem.monthly_cost}` : 'N/A'}
                    </Text>
                  </Box>
                </SimpleGrid>
                
                {selectedItem.api_response_data && (
                  <Accordion allowToggle mt={4}>
                    <AccordionItem>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            API Response Data
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel>
                        <Box maxHeight="300px" overflow="auto">
                          <pre style={{ whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(selectedItem.api_response_data, null, 2)}
                          </pre>
                        </Box>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                )}
                
                {selectedItem.ai_responses && (
                  <Accordion allowToggle mt={4}>
                    <AccordionItem>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            AI Responses
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel>
                        <Box maxHeight="300px" overflow="auto">
                          <pre style={{ whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(selectedItem.ai_responses, null, 2)}
                          </pre>
                        </Box>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                )}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onDetailsModalClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Deletion</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete this search history item? This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleDeleteItem}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SearchHistory;
